import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, FunnelIcon } from '@heroicons/react/24/outline';

// Status color mapping for opposition changes
const STATUS_COLORS = {
  'Abandoned': '#46c37b',
  'Accepted & Advertised': '#70b9eb',
  'Advertised bef acc': '#5c90d2',
  'Exam Report Issued': '#f3b760',
  'Cancelled': '#d26a5c',
  'Formalities Chk Fail': '#d26a5c',
  'Formalities Chk Pass': '#46c37b',
  'Marked for Exam': '#f3b760',
  'New Application': '#5c90d2',
  'Objected': '#d26a5c',
  'Opposed': '#d26a5c',
  'Refused': '#f3b760',
  'Registered': '#70b9eb',
  'Removed': '#d26a5c',
  'Send Back to EDP': '#d26a5c',
  'Send to PRAS': '#46c37b',
  'Send To Vienna Codification': '#d26a5c',
  'Withdrawn': '#5c90d2',
  'Withdraw Found': '#5c90d2',
  'Counter Filed': '#f3b760',
  'Notice Filed': '#d26a5c',
  'Evidence Filed': '#5c90d2',
  'Hearing Scheduled': '#f3b760',
  'Decision Pending': '#f3b760',
  'Opposition Dismissed': '#46c37b',
  'Opposition Allowed': '#d26a5c'
};

const OppositionChanges = () => {
  const navigate = useNavigate();
  const [changesData, setChangesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Set default to today's date
  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format for date picker
    setFromDate(formattedDate);
    setToDate(formattedDate);
  }, []);

  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    // Convert YYYY-MM-DD (from date picker) to d/m/Y format for API
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // d/m/Y format
  };

  const getLast7Days = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    // Format for date picker (YYYY-MM-DD)
    const formatDateForPicker = (date) => {
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };
    
    setFromDate(formatDateForPicker(sevenDaysAgo));
    setToDate(formatDateForPicker(today));
  };

  const getLast30Days = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    // Format for date picker (YYYY-MM-DD)
    const formatDateForPicker = (date) => {
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };
    
    setFromDate(formatDateForPicker(thirtyDaysAgo));
    setToDate(formatDateForPicker(today));
  };

  const fetchChangesData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📡 Fetching opposition changes data...');
      
      const params = {
        url: `https://agent3.tmpilot.com/api/oppositions/changes?token={token}&page=${page}&per_page=10`
      };

      // Add date filters if provided
      if (fromDate) {
        params.url += `&dateFrom=${formatDateForAPI(fromDate)}`;
      }
      if (toDate) {
        params.url += `&dateTo=${formatDateForAPI(toDate)}`;
      }

      const response = await api.post('/api-data/get-data', params);

      if (response.data.success) {
        console.log('✅ Opposition changes data received:', response.data.data);
        const data = response.data.data;
        
        if (data.data && Array.isArray(data.data)) {
          setChangesData(data.data);
          setCurrentPage(data.current_page || page);
          setTotalPages(data.last_page || 1);
          setTotalRecords(data.total || 0);
          toast.success(`Loaded ${data.data.length} opposition changes records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load opposition changes data');
      }
    } catch (error) {
      console.error('Error fetching opposition changes:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load opposition changes data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchChangesData(1);
  }, [fetchChangesData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchChangesData(page);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchChangesData(1);
  };

  const handleAppNoClick = (appno) => {
    navigate(`/api-data/trademark-portfolio/${appno}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return dateString;
    }
  };

  // Get status color for styling
  const getStatusColor = (status) => {
    if (!status) return '#6b7280'; // Default gray color
    return STATUS_COLORS[status] || '#6b7280'; // Return mapped color or default gray
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages.map((pageNum, index) => {
      if (pageNum === '...') {
        return (
          <span
            key={`ellipsis-${index}`}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
          >
            ...
          </span>
        );
      }
      
      return (
        <button
          key={pageNum}
          onClick={() => handlePageChange(pageNum)}
          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
            pageNum === currentPage
              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
        >
          {pageNum}
        </button>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Opposition Changes</h1>
              <p className="mt-1 text-sm text-gray-500">
                Track changes made to opposition proceedings
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              <button
                onClick={() => fetchChangesData(currentPage)}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Options</h3>
            <form onSubmit={handleFilterSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div className="flex items-end space-x-2">
                  <button
                    type="button"
                    onClick={getLast7Days}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Last 7 Days
                  </button>
                  <button
                    type="button"
                    onClick={getLast30Days}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Last 30 Days
                  </button>
                </div>
                
                <div className="flex items-end space-x-2">
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Opposition Changes Records ({totalRecords} total)
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Showing page {currentPage} of {totalPages}
            </p>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-blue-500 hover:bg-blue-400 transition ease-in-out duration-150 cursor-not-allowed">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading opposition changes data...
              </div>
            </div>
          ) : changesData.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No opposition changes found for the selected criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Application No / Opp Number
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Name / TM Name / Class / Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Old Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      New Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date Changed
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {changesData.map((change, index) => (
                    <tr key={change.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <button
                            onClick={() => handleAppNoClick(change.appno)}
                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer font-medium"
                          >
                            {change.appno}
                          </button>
                          <br />
                          <span className="text-gray-500 text-xs">
                            Opp: {change.opposition_id || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{change.trademark?.buisnessName || '-'}</div>
                          <div className="text-gray-600">{change.trademark?.tmAppliedFor || '-'}</div>
                          <div className="text-gray-500 text-xs">
                            Class: {change.trademark?.class || '-'} | Type: {change.trademark?.type || '-'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {change.field === 'status' ? (
                          <span 
                            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                            style={{ backgroundColor: getStatusColor(change.old_value) }}
                          >
                            {change.old_value || 'Empty'}
                          </span>
                        ) : (
                          change.old_value || 'Empty'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {change.field === 'status' ? (
                          <span 
                            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                            style={{ backgroundColor: getStatusColor(change.new_value) }}
                          >
                            {change.new_value || 'Empty'}
                          </span>
                        ) : (
                          change.new_value || 'Empty'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(change.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span> pages
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  
                  {renderPagination()}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OppositionChanges; 